import { useQuery } from "@tanstack/react-query";
import { UserIcon } from "lucide-react";
import { useMemo } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";
import type { AdminRole } from "@upclaim/sdk/src/extra-types";

import { Combobox } from "@/components/combobox";
import { Label } from "@/components/ui/label";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

/** Shape actually returned by GET /admin/admins (the SDK types it as object[]). */
interface Admin {
  id: string;
  fullName: string;
  email?: string | null;
  profilePictureUrl?: string | null;
  adminRole?: { id: number; name: string };
}

interface AdminComboboxProps {
  value: string | null;
  onChange: (adminId: string | null) => void;
  label?: string;
  placeholder?: string;
  /** Roles listed in the picker — Upclaim members by default, `null` for everyone. */
  filterRoles?: AdminRole[] | null;
}

/** Port of AdminCombobox.vue — searchable admin picker backed by adminAPI.listAdmin. */
export function AdminCombobox({
  value,
  onChange,
  label = "Assigner à",
  placeholder = "Sélectionner un admin",
  filterRoles = UPCLAIM_ROLES,
}: AdminComboboxProps) {
  const { data: adminList = [], isPending } = useQuery({
    queryKey: queryKeys.search.admins,
    queryFn: async () => (await adminAPI.listAdmin()) as Admin[],
  });

  const admins = useMemo(
    () =>
      filterRoles
        ? adminList.filter((admin) => filterRoles.includes(admin.adminRole?.name as AdminRole))
        : adminList,
    [adminList, filterRoles],
  );

  const selectedAdmin = admins.find((admin) => admin.id === value) ?? null;

  const emptyMessage = isPending
    ? "Chargement..."
    : admins.length
      ? "Aucun admin trouvé"
      : "Aucun admin disponible";

  return (
    <div>
      {label ? <Label className="mb-2 block text-sm font-medium text-gray-900">{label}</Label> : null}
      <Combobox
        items={admins}
        value={selectedAdmin}
        onChange={(admin) => onChange(admin?.id ?? null)}
        getKey={(admin) => admin.id}
        getLabel={(admin) => admin.fullName}
        getSearchValue={(admin) => admin.email ?? ""}
        renderItem={(admin) => (
          <>
            {admin.profilePictureUrl ? (
              <img
                src={admin.profilePictureUrl}
                alt=""
                className="size-6 shrink-0 rounded-full bg-gray-100"
              />
            ) : (
              <span className="grid size-6 shrink-0 place-items-center rounded-full bg-gray-300">
                <UserIcon className="size-4 fill-white text-white" aria-hidden="true" />
              </span>
            )}
            <span className="ml-1 truncate">{admin.fullName}</span>
          </>
        )}
        placeholder={placeholder}
        searchPlaceholder="Rechercher..."
        emptyMessage={emptyMessage}
      />
    </div>
  );
}
